import React, { useState, useRef, useEffect } from 'react';
import { Provider, useDispatch } from 'react-redux';
import jwt_decode from 'jwt-decode';
import * as yup from 'yup';
import { Form, Formik } from 'formik';
import { Button, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

import appapi from '../../../api/apiClient';
import { showmessage } from '../../../utils/toastr';
import { login } from '../../../redux/features/authSlice';
import { buttonStyle1 } from '../../../utils/commonStyle';
import { setLoading } from '../../../redux/features/appSlice';
import FormTextField from '../../../components/FormTextField';
import AuthLayout from '../../../components/AuthLayout';
import LanguageSwitcher from '../../../components/LanguageSwitcher';

/**
 * Validation Schema for register
 */
const validationSchema = yup.object({
  email: yup
    .string('Enter your Email')
    .matches(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/, '*Invalid Email Address')
    .required('')
    .trim(),
  password: yup
    .string('Enter your Password')
    // .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,12}$/, 'Enter a password of minimum 8 characters with a combination of capital letters, small letters, symbols, and numbers.')
    .required('')
    .trim(),
});

export default function Login() {
  const theme = useTheme();

  const dispatch = useDispatch();

  const { t } = useTranslation();

  const isMobile = useMediaQuery('(max-width:1000px)');

  const formRef = useRef();

  const navigate = useNavigate();
  const [visibility, setVisibility] = useState(false);

  const { state } = useLocation();

  const [data, setData] = useState({
    email: state?.email ? state?.email : '',
    password: '',
  });

  /**
   * @param {email, password} obj
   * Login user
   */

  const loginApi = async (obj) => {
    dispatch(setLoading(true));
    let payload = {
      ...obj,
      deviceId: 'abc123',
      userType: 'customer',
    };
    const response = await appapi.post('auth/login', payload);
    if (response.ok) {
      dispatch(setLoading(false));
      const data = response.data;
      if (data.status === '7400') {
        // showmessage(data.message)
        localStorage.setItem('accessToken', data.response.accessToken);
        localStorage.setItem('refreshToken', data.response.refreshToken);
        appapi.setHeader('Authorization', `Bearer ${data.response.accessToken}`);
        const decoded = jwt_decode(data.response.accessToken);
        dispatch(login(decoded.sub));
      } else {
        showmessage(data.message, 'error');
      }
    } else {
      dispatch(setLoading(false));
      if (response.status === 400) {
        let error = response.data.errors[0].message.replace('body.emailId', 'Email Id');
        showmessage(error, 'error');
      }
    }
  };

  /**
   *
   * @returns redirect to forgotPassword Page
   */
  const onClickForgotPassword = (values, errors) => {
    if (!Boolean(errors.email)) {
      navigate(`/forgotPassword`, { state: { email: values.email } });
    } else {
      navigate(`/forgotPassword`, { state: { email: '' } });
    }
  };

  const loginForm = () => {
    return (
      <>
        <Stack style={{ backgroundColor: '#FFFFFF', borderRadius: '8px' }}>
          <Typography
            className="fw-medium ff-Roboto createHeading !mt-[16px] !mx-[16px]"
            color={theme.palette.primary.text}
            fontSize={'16px'}
          >
            {t('auth.Login')}
          </Typography>
          <Stack style={{ backgroundColor: '#F2F2F2', width: '100%', height: '1px', marginTop: '12px' }} />
          <Formik
            innerRef={formRef}
            enableReinitialize={true}
            initialValues={{
              email: data?.email || '',
              password: data?.password,
            }}
            validationSchema={validationSchema}
            onSubmit={(values) => loginApi(values)}
          >
            {({ handleSubmit, values, handleChange, errors, touched, handleBlur, isValid, dirty }) => (
              <Form
                noValidate
                autoComplete="off"
              >
                <Stack
                  flex={1}
                  className="mt-[16px] mx-[16px]"
                >
                  <FormTextField
                    type="email"
                    outlineLabel={t('auth.Email')}
                    name="email"
                    sx={{ mb: 0 }}
                    lowerCase={true}
                  />
                </Stack>
                <Stack
                  flex={1}
                  className="mt-14 mx-[16px]"
                >
                  <FormTextField
                    outlineLabel={t('auth.Password')}
                    name="password"
                    type="password"
                    sx={{ mb: '14px' }}
                    // forgotPassword={true}
                    onClickForgotPassword={() => onClickForgotPassword(values, errors)}
                    visibility={visibility}
                    setVisibility={(val) => setVisibility(val)}
                  />
                </Stack>

                <Stack className="px-[16px] pb-[16px]">
                  <Button
                    sx={{
                      ...buttonStyle1,
                      width: '100%',
                      mt: '2.3px',
                      backgroundColor: theme.palette.primary.main,
                      color: '#fff',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                        color: '#fff',
                      },
                      '&:disabled': {
                        color: 'rgba(255,255,255,0.5)',
                        backgroundColor: theme.palette.primary.main,
                      },
                    }}
                    disabled={!(dirty && isValid)}
                    type="submit"
                  >
                    {t('auth.Login')}
                  </Button>
                  <Stack
                    direction={'row'}
                    alignItems={'center'}
                    className="mt-[16px]"
                  >
                    <Typography
                      className="fw-medium ff-Roboto"
                      color={theme.palette.primary.text}
                      fontSize={'12px'}
                    >
                      {t('auth.LoginForgotPassword')}
                    </Typography>
                    <Stack onClick={() => onClickForgotPassword(values, errors)}>
                      <Typography
                        className="fw-medium ff-Roboto"
                        color={theme.palette.primary.light}
                        fontSize={'12px'}
                        style={{ marginLeft: '3px', cursor: 'pointer' }}
                      >
                        {t('auth.Reset')}
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>
              </Form>
            )}
          </Formik>
        </Stack>
      </>
    );
  };

  return (
    <AuthLayout>
      <Stack
        style={{ maxWidth: '300px', minWidth: '300px' }}
        direction="row"
        alignSelf="center"
      >
        <Stack
          className="leftStack"
          style={{ width: '100%' }}
        >
          <Stack className="formSubmittedStyle mt-28">
            <Stack>
              <Typography
                color={'#FFFFFF'}
                className="fw-medium ff-Roboto"
                style={{ fontSize: '24px', lineHeight: '24px' }}
              >
                {t('CarbonWallet')}
              </Typography>
              <Typography
                color={'#FFFFFF'}
                className="fw-medium ff-Roboto"
                style={{ fontSize: '16px', lineHeight: '24px' }}
              >
                {t('By ZeroEmissions')}
              </Typography>
            </Stack>
            <div style={{ marginTop: '24px' }}>{loginForm()}</div>
          </Stack>
        </Stack>
      </Stack>
      <div style={{ position: 'absolute', top: '16px', right: '16px' }}>
        <LanguageSwitcher />
      </div>
    </AuthLayout>
  );
}
